/**
 * domainRegistry — CRUD for schools/{schoolId}/domains/
 *
 * Behaviour (`discipline`) and Recognition are centrally governed Domains with
 * protected runtime bindings. Protection does not make their school-owned
 * configuration read-only.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  getCountFromServer,
  deleteField,
} from 'firebase/firestore';
import { db, functions, httpsCallable } from '../firebase';
import {
  ALWAYS_ON_PROTECTED_IDS,
  CENTRAL_GOVERNANCE_EXCLUDED_DOMAIN_IDS,
  PROTECTED_DOMAIN_DEFINITIONS,
  PROTECTED_DOMAIN_IDS,
  SCHOOL_SETTABLE_PROTECTED_KEYS,
} from './domain/protectedDefinitions';
import {
  CENTRAL_DOMAINS_MODEL,
  buildSchoolGovernanceInventory,
  canConfigure,
  canConfigureDomainCapability,
  capabilityIsEnabled,
  compareDomainConfigs,
  deriveDomainConfiguration,
  domainSupportsSurface,
  isImmutableDomainUpdatePath,
} from './domain/centralModel';
import { dynamicDomainListConstraints } from './domain/dynamicDomainQueries';
import {
  CENTRAL_DOMAIN_DEFINITIONS,
  deriveDomainCollections,
  domainDefinitionForStoredDomain,
  getCentralDomainDefinition,
  materializeGovernedDomain,
  resolveStoredGovernedDomain,
} from './domain/centralDefinitions';
import { normaliseFields } from './domain/optionUtils';
import { mergeDomainFieldChanges } from './domain/fieldConfigurationMerge';
import {
  getDomainCollectionBinding,
} from './domain/collectionBindingReservations';

// Preserve the public registry export while keeping the definitions themselves
// independent of Firebase and usable by migration/audit tooling.
export { PROTECTED_DOMAIN_DEFINITIONS } from './domain/protectedDefinitions';

/**
 * Validate a domain creation input.
 * @param {Object} input
 * @throws {Error} on invalid input
 */
function validateInput(input) {
  if (!input || !input.name || typeof input.name !== 'string' || !input.name.trim()) {
    throw new Error('Domain name is required');
  }
  if (!/[a-z0-9]/i.test(input.name)) {
    throw new Error('Domain name must contain at least one letter or number');
  }
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(input.name)) {
    throw new Error('Domain name must be a lowercase hyphenated stable name');
  }
  if (PROTECTED_DOMAIN_IDS.includes(input.name.toLowerCase().trim())) {
    throw new Error(`Cannot create domain with protected name: "${input.name}"`);
  }
  if (!input.label || typeof input.label !== 'string' || !input.label.trim()) {
    throw new Error('Domain label is required');
  }
}

/**
 * Get the Firestore collection ref for a school's domains.
 * @param {string} schoolId
 * @returns {CollectionReference}
 */
function domainsRef(schoolId) {
  return collection(db, 'schools', schoolId, 'domains');
}

/**
 * Get the Firestore doc ref for a specific domain.
 * @param {string} schoolId
 * @param {string} domainId
 * @returns {DocumentReference}
 */
function domainDocRef(schoolId, domainId) {
  return doc(db, 'schools', schoolId, 'domains', domainId);
}

/**
 * Firestore rejects undefined object properties. Legacy materialised Domains
 * can legitimately omit a newer Prototype path, which means “inherit” rather
 * than “persist undefined”. Strip those absent object properties at this
 * compatibility write boundary while refusing sparse arrays, whose positions
 * may carry stable ordering semantics.
 */
function withoutUndefinedObjectValues(value, path = '') {
  if (Array.isArray(value)) {
    return value.map((entry, index) => {
      if (entry === undefined) {
        throw new Error(`Invalid undefined Domain list value at ${path}[${index}]`);
      }
      return withoutUndefinedObjectValues(entry, `${path}[${index}]`);
    });
  }
  if (!value || typeof value !== 'object') return value;
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([, entry]) => entry !== undefined)
    .map(([key, entry]) => [
      key,
      withoutUndefinedObjectValues(entry, path ? `${path}.${key}` : key),
    ]));
}

async function commitDomainConfiguration(data) {
  const callable = httpsCallable(functions, 'commitDomainConfiguration');
  const response = await callable(data);
  return response?.data || {};
}

/**
 * Resolve every stored Domain through the central model. Behaviour and
 * Recognition still use canonical in-code runtime bindings, but receive the
 * same Prototype/configuration metadata as every other Domain.
 * Stored copies exist for backend tools that iterate Firestore directly, but
 * the in-app source of truth is the central Prototype catalog. This means:
 *
 *   - A stale Firestore doc (old `fields` array, missing `primary`) never
 *     reaches consumers; they see the up-to-date canonical shape.
 *   - Missing protected-runtime documents are installed explicitly by the
 *     governed onboarding boundary; an ordinary read never attempts a write.
 *   - Runtime identity remains protected while the Prototype policy still
 *     exposes deliberate school configuration paths.
 */
/**
 * Stored-doc keys a school IS allowed to set on a protected domain. The schema
 * (fields, primary, collections, …) always comes from the canonical in-code
 * def, but these opt-in flags are the school's to flip and must survive the
 * synthesize-from-canonical step. Keep this list tiny and explicit.
 */
function synthesizeDomains(docs, onInvalidDomain) {
  const resolveDomain = (stored) => {
    try {
      return resolveStoredGovernedDomain(stored);
    } catch (error) {
      // Ordinary reads/mutations remain strict. A selector subscription can
      // isolate an unavailable Domain and explicitly report it to the UI.
      if (!onInvalidDomain) throw error;
      onInvalidDomain(stored.id, error);
      return null;
    }
  };
  const storedById = new Map(docs.map(d => [d.id, d]));
  const protectedOut = PROTECTED_DOMAIN_IDS
    .filter(id => PROTECTED_DOMAIN_DEFINITIONS[id])
    .map(id => {
      const stored = storedById.get(id) || {};
      const canonicalProjection = { id, ...PROTECTED_DOMAIN_DEFINITIONS[id] };
      for (const key of SCHOOL_SETTABLE_PROTECTED_KEYS) {
        if (stored[key] !== undefined) canonicalProjection[key] = stored[key];
      }
      if (stored.governance) canonicalProjection.governance = stored.governance;
      if (stored.configuration) canonicalProjection.configuration = stored.configuration;
      for (const key of ['createdAt', 'createdBy', 'updatedAt']) {
        if (stored[key] !== undefined) canonicalProjection[key] = stored[key];
      }
      return resolveDomain(canonicalProjection);
    });
  const governedInstalled = docs
    .filter((domain) => !PROTECTED_DOMAIN_IDS.includes(domain.id))
    .map(resolveDomain);
  return [...protectedOut, ...governedInstalled]
    .filter(Boolean)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/**
 * Compare today's registry result with the versioned central model without
 * changing the value returned to callers. This is intentionally pure so the
 * same report can be produced in tests, the browser shadow path, and the
 * read-only inventory command.
 */
export function buildDomainGovernanceShadowReport(schoolId, storedDomains, currentDomains) {
  return buildSchoolGovernanceInventory({
    schoolId,
    storedDomains,
    centralDefinitions: CENTRAL_DOMAIN_DEFINITIONS,
    protectedDomainIds: [],
    excludedDomainIds: CENTRAL_GOVERNANCE_EXCLUDED_DOMAIN_IDS,
    schoolSettableProtectedKeys: SCHOOL_SETTABLE_PROTECTED_KEYS,
    currentDomains: currentDomains || synthesizeDomains(storedDomains),
  });
}

/**
 * Get all domains for a school.
 *
 * The canonical definitions are merged into the returned list. Persisted
 * installation is deliberately not attempted here: reads are available to
 * staff, while structural writes require the trusted administrator boundary.
 *
 * @param {string} schoolId
 * @returns {Promise<DomainConfig[]>}
 */
export async function getDomains(schoolId) {
  if (!schoolId) throw new Error('getDomains: schoolId is required');

  const ref = domainsRef(schoolId);
  const q = query(ref, orderBy('sortOrder'));
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));

  const effectiveDomains = synthesizeDomains(docs);
  if (import.meta.env.VITE_DOMAIN_GOVERNANCE_SHADOW === 'true') {
    const report = buildDomainGovernanceShadowReport(schoolId, docs, effectiveDomains);
    if (report.summary.behaviorDifferences > 0) {
      console.warn('[domainRegistry] central-model shadow differences', report);
    }
  }
  return effectiveDomains;
}

/**
 * Get a single domain by ID.
 * @param {string} schoolId
 * @param {string} domainId
 * @returns {Promise<DomainConfig|null>}
 */
export async function getDomain(schoolId, domainId) {
  if (!schoolId) throw new Error('getDomain: schoolId is required');
  if (!domainId) throw new Error('getDomain: domainId is required');

  const ref = domainDocRef(schoolId, domainId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const stored = { ...snap.data(), id: snap.id };
  if (PROTECTED_DOMAIN_IDS.includes(domainId)) {
    return synthesizeDomains([stored]).find((domain) => domain.id === domainId) || null;
  }
  return resolveStoredGovernedDomain(stored);
}

async function getStoredDomain(schoolId, domainId) {
  const snap = await getDoc(domainDocRef(schoolId, domainId));
  return snap.exists() ? { ...snap.data(), id: snap.id } : null;
}

/**
 * Create a new (non-protected) domain.
 * Auto-generates collection names from the domain name.
 * @param {string} schoolId
 * @param {Object} input - DomainCreationInput
 * @param {string} [createdBy='user'] - userId or 'agent'
 * @returns {Promise<DomainConfig>}
 */
export async function createDomain(schoolId, input, createdBy = 'user') {
  if (!schoolId) throw new Error('createDomain: schoolId is required');

  validateInput(input);

  const name = input.name.trim();
  const parentDomainId = input.parentDomainId || null;
  const labelKey = input.label.trim().toLowerCase();
  const collections = deriveDomainCollections(name);
  const normalizedName = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const centralDefinition = getCentralDomainDefinition(input.definitionId)
    || getCentralDomainDefinition(normalizedName);
  if (centralDefinition) {
    throw new Error(
      `The central Domain "${name}" must be installed through Swan's governed setup service.`,
    );
  }

  // A Domain's stable slug owns its generated collections for the lifetime of
  // the installation. Archiving hides a Domain; it does not release those
  // paths, because historical events, Actions, rules and audit entries still
  // live there. Compare the materialised bindings as well as `name` so older
  // documents and equivalent slugs (for example `reading-club` and
  // `reading_club`) cannot be pointed at the same collections.
  const requestedCollectionPaths = new Set(Object.values(collections));
  // Compare on display label, not the slugified `name` — callers (e.g. the
  // PersistentContextBar) auto-suffix `name` for collection-path uniqueness,
  // which would otherwise let two domains with the same visible label coexist.
  const existing = await getDocs(query(domainsRef(schoolId)));
  const collectionClash = existing.docs.find((domainDoc) => {
    const data = domainDoc.data();
    const bound = new Set([
      ...Object.values(data?.collections || {}),
      ...Object.values(data?.governance?.bindings?.collections || {}),
      data?.eventsCollection,
      data?.rulesCollection,
      data?.actionsCollection,
      data?.ruleFiresCollection,
    ].filter((value) => typeof value === 'string' && value));
    if (typeof data?.name === 'string' && data.name.trim()) {
      Object.values(deriveDomainCollections(data.name)).forEach((path) => bound.add(path));
    }
    return [...requestedCollectionPaths].some((path) => bound.has(path));
  });
  if (collectionClash) {
    const archived = collectionClash.data()?.active === false;
    throw new Error(
      archived
        ? `The Domain name "${name}" is reserved by an archived Domain. Restore that Domain or choose a different name.`
        : `A Domain using the name "${name}" already exists and owns its event collections. Choose a different name.`,
    );
  }

  const labelClash = existing.docs.find(d => {
    const data = d.data();
    if ((data?.parentDomainId || null) !== parentDomainId) return false;
    return (data?.label || '').trim().toLowerCase() === labelKey;
  });
  if (labelClash) {
    if (labelClash.data()?.active === false) {
      throw new Error(
        `A Domain called "${input.label.trim()}" is archived here. Restore it or choose a different name.`,
      );
    }
    const where_ = parentDomainId ? 'in this group' : 'at the top level';
    throw new Error(`A domain called "${input.label.trim()}" already exists ${where_}.`);
  }

  const now = Timestamp.now();

  const domainData = {
    schoolId,
    name,
    label: input.label.trim(),
    description: input.description || '',
    protected: false,
    active: true,
    sortOrder: input.sortOrder ?? 10,
    color: input.color || 'primary',
    // New shape (defaultView + fieldType) or legacy shape (defaultInputMethod).
    // Canonical domains (primary + followUp) carry NO top-level fieldType — the
    // field's own `type` is the source of truth, exactly as the protected health/
    // attendance configs are shaped. Forcing fieldType:'multiChoice' here would
    // mislabel a number-primary preset (e.g. Score) for any caller of
    // getDomainFieldType, even though useMultiField currently bypasses it.
    ...(input.defaultView
      ? {
          defaultView: input.defaultView,
          ...(input.primary ? {} : { fieldType: input.fieldType || 'multiChoice' }),
        }
      : { defaultInputMethod: input.defaultInputMethod || 'individual' }
    ),
    ...(input.maxScore != null ? { maxScore: input.maxScore } : {}),
    ...(input.currencyId ? { currencyId: input.currencyId } : {}),
    ...(input.parentDomainId ? { parentDomainId: input.parentDomainId } : {}),
    ...(Array.isArray(input.inputSurfaces) ? { inputSurfaces: input.inputSurfaces } : {}),
    ...(input.capabilities ? { capabilities: input.capabilities } : {}),
    ...(input.surfaces ? { surfaces: input.surfaces } : {}),
    // Canonical shape (preset-derived or builder-authored): primary + followUp.
    // Takes precedence over the legacy `fields[]` branch below.
    ...(input.primary
      ? {
          primary: normaliseFields([input.primary])[0],
          followUp: normaliseFields(
            (Array.isArray(input.followUp) ? input.followUp : []).map((f, i) => ({ order: i, ...f })),
          ),
        }
      : Array.isArray(input.fields) && input.fields.length > 0
        ? { fields: normaliseFields(input.fields.map((f, i) => ({ order: i, ...f }))) }
        : {}),
    terminology: input.terminology || {
      event: { label: 'event', plural: 'events', description: '' },
      category: { label: 'category', plural: 'categories', description: '' },
      type: { label: 'type', plural: 'types', description: '' },
      action: { label: 'action', plural: 'actions', description: '' },
      rule: { label: 'rule', plural: 'rules', description: '' },
      queue: { label: 'queue', plural: 'queues', description: '' },
    },
    contextTags: input.contextTags || {},
    scope: input.scope || { grades: [] },
    collections,
    eventsCollection: collections.events,
    ruleFiresCollection: collections.actions.replace(/_actions$/, '_ruleFires'),
    createdBy,
    createdAt: now,
    updatedAt: now,
  };

  const governed = materializeGovernedDomain({
    id: name,
    ...domainData,
    governance: {
      definitionId: `school.${schoolId}.${parentDomainId || 'root'}.${name}`,
    },
  });
  const stored = { ...governed };
  delete stored.id;
  stored.schoolId = schoolId;
  // Firestore rules compare the mutable school configuration with the copy in
  // governance so neither client nor resolver can smuggle a different policy
  // interpretation through one side of the document.
  stored.configuration = governed.governance.configuration;
  const binding = getDomainCollectionBinding(stored);
  if (!binding) throw new Error(`Domain collection binding is unavailable: ${name}`);
  stored.collectionBindingId = binding.id;
  const writePayload = withoutUndefinedObjectValues(stored);
  const result = await commitDomainConfiguration({
    operation: 'create',
    schoolId,
    name,
    configuration: writePayload.configuration,
    active: writePayload.active !== false,
  });
  if (!result.domainId) {
    throw new Error('The governed Domain service did not return a Domain id');
  }
  return {
    ...writePayload,
    id: result.domainId,
    governance: {
      ...writePayload.governance,
      configurationRevision: result.configurationRevision ?? 0,
    },
  };
}

/**
 * Atomically install exact centrally-authored Domain Definitions. The server
 * owns canonical payloads and bindings; callers select IDs only.
 * @param {string} schoolId Target school.
 * @param {string[]} definitionIds Exact central Definition IDs.
 * @param {string|null} requestId Optional idempotency/audit correlation id.
 * @returns {Promise<{installed: Array, requestId: string|null}>}
 */
export async function installDomainDefinitions(
  schoolId,
  definitionIds,
  requestId = null,
) {
  if (!schoolId) throw new Error('installDomainDefinitions: schoolId is required');
  if (!Array.isArray(definitionIds) || definitionIds.length === 0) {
    throw new Error('installDomainDefinitions: definitionIds are required');
  }
  return commitDomainConfiguration({
    operation: 'install',
    schoolId,
    definitionIds,
    ...(requestId ? { requestId } : {}),
  });
}

/**
 * Compatibility facade for installing the two protected-runtime Domains.
 * Canonical payloads, immutable bindings, reservations and auditing are owned
 * by the same trusted server boundary as every other Domain installation.
 *
 * @param {string} schoolId
 * @returns {Promise<{discipline: boolean, recognition: boolean}>} - true if a
 *   doc was created or updated, false if no change was needed.
 */
export async function seedProtectedDomains(schoolId) {
  if (!schoolId) throw new Error('seedProtectedDomains: schoolId is required');
  const definitionIds = PROTECTED_DOMAIN_IDS.map((id) => {
    const definition = getCentralDomainDefinition(id);
    if (!definition?.id) throw new Error(`Missing canonical Domain Definition: ${id}`);
    return definition.id;
  });
  const response = await installDomainDefinitions(schoolId, definitionIds);
  const installedByDefinition = new Map(
    (response.installed || []).map((entry) => [entry.definitionId, entry]),
  );
  return Object.fromEntries(PROTECTED_DOMAIN_IDS.map((id, index) => [
    id,
    installedByDefinition.get(definitionIds[index])?.created === true,
  ]));
}

/**
 * Update a Domain through its Prototype configuration policy. The resulting
 * v4 configuration delta is stored alongside a materialised v3 projection so
 * existing readers continue to work while future Prototype additions inherit.
 * @param {string} schoolId
 * @param {string} domainId
 * @param {Object} updates - Partial<DomainConfig>
 * @param {Object} [options] Optional field baseline or expectedConfigurationRevision
 *   for callers editing a confirmed configuration snapshot. Omitted revision
 *   preserves existing callers; the server transaction still checks its revision.
 * @returns {Promise<Object|void>} Acknowledged fields and revision for configuration writes.
 */
export async function updateDomain(schoolId, domainId, updates, { baseFields, expectedConfigurationRevision } = {}) {
  if (!schoolId) throw new Error('updateDomain: schoolId is required');
  if (!domainId) throw new Error('updateDomain: domainId is required');

  const current = await getStoredDomain(schoolId, domainId);
  if (!current) throw new Error(`Domain not found: ${domainId}`);
  if (!updates || typeof updates !== 'object' || Array.isArray(updates)) {
    throw new Error('updateDomain: updates must be an object');
  }
  // Validate the persisted binding before resolving any configurable fields.
  // A malformed Admin-SDK write must not be normalised into an apparently
  // valid browser update or allowed to claim a reservation on another path.
  getDomainCollectionBinding(current);

  // Cycle guard: if reparenting, walk the chain and reject if we'd loop back.
  if (Object.prototype.hasOwnProperty.call(updates, 'parentDomainId') && updates.parentDomainId) {
    if (updates.parentDomainId === domainId) {
      throw new Error('A domain cannot be its own parent.');
    }
    const seen = new Set([domainId]);
    let cursor = updates.parentDomainId;
    while (cursor) {
      if (seen.has(cursor)) {
        throw new Error('Parent chain would create a cycle.');
      }
      seen.add(cursor);
      const next = await getStoredDomain(schoolId, cursor);
      if (!next) break;
      cursor = next.parentDomainId || null;
    }
  }

  // Normalise field payloads so option shapes and type names never regress on
  // update. Two shapes can arrive:
  //   - canonical: `primary` (single field) + `followUp` (array) — what the
  //     builder now writes. When present, also drop any stale `fields` array in
  //     the SAME write so a domain never carries both shapes side-by-side.
  //   - legacy: `fields[]` — still accepted from older callers.
  const writesCanonical =
    Object.prototype.hasOwnProperty.call(updates, 'primary') ||
    Object.prototype.hasOwnProperty.call(updates, 'followUp');

  let normalisedUpdates = updates;
  if (writesCanonical) {
    normalisedUpdates = { ...updates };
    if (Object.prototype.hasOwnProperty.call(updates, 'primary')) {
      const [normalisedPrimary] = normaliseFields([updates.primary]);
      normalisedUpdates.primary = normalisedPrimary ?? updates.primary;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'followUp')) {
      normalisedUpdates.followUp = normaliseFields(updates.followUp);
    }
    // Retire the obsolete array unless the caller is explicitly setting it.
    if (!Object.prototype.hasOwnProperty.call(updates, 'fields') && Array.isArray(current.fields)) {
      normalisedUpdates.fields = deleteField();
    }
  } else if (Array.isArray(updates?.fields)) {
    normalisedUpdates = { ...updates, fields: normaliseFields(updates.fields) };
  }

  const definition = domainDefinitionForStoredDomain(current);
  const currentEffective = resolveStoredGovernedDomain(current);
  if (expectedConfigurationRevision !== undefined) {
    if (!Number.isSafeInteger(expectedConfigurationRevision) || expectedConfigurationRevision < 0) {
      throw new Error('Expected Domain configuration revision must be a non-negative integer');
    }
    if (expectedConfigurationRevision !== (currentEffective.governance?.configurationRevision || 0)) {
      throw Object.assign(new Error('Recording settings changed in another session. Retry to apply your change to the latest settings.'), {
        code: 'domain/configuration-conflict',
      });
    }
  }
  if (writesCanonical && baseFields) {
    const currentFields = { primary: currentEffective.primary, followUp: currentEffective.followUp || [] };
    const { merged, conflicts } = mergeDomainFieldChanges(baseFields, {
      primary: normalisedUpdates.primary ?? baseFields.primary,
      followUp: normalisedUpdates.followUp ?? baseFields.followUp,
    }, currentFields);
    if (conflicts.length) {
      const error = new Error('This form changed in another session. Compare the changes before saving.');
      error.code = 'domain/field-conflict';
      error.conflicts = conflicts;
      error.currentFields = currentFields;
      error.mergedFields = merged;
      throw error;
    }
    normalisedUpdates = { ...normalisedUpdates, ...merged };
  }
  const overviewKeys = new Set(definition.configurationPolicy.overview.paths || []);
  const eventKeys = new Set(['primary', 'followUp', 'fields', 'listBlocks']);
  const actionDefinitionKeys = new Set([
    'actionDefinitions',
  ]);
  const parentMessageKeys = new Set([
    'parentMessages',
    'defaultMessageActionId',
    'defaultActionId',
  ]);
  const actionKeys = new Set([...actionDefinitionKeys, ...parentMessageKeys]);
  const compatibilityKeys = new Set();
  const structuralKeys = new Set([
    'id',
    'schoolId',
    'name',
    'collections',
    'eventsCollection',
    'rulesCollection',
    'actionsCollection',
    'ruleFiresCollection',
    'runtime',
    'protected',
    'collectionBindingId',
    'governance',
    'configuration',
  ]);
  for (const key of Object.keys(normalisedUpdates)) {
    if (structuralKeys.has(key) || isImmutableDomainUpdatePath(key)) {
      throw new Error(`Cannot change governed Domain identity or runtime binding: ${key}`);
    }
    if (key === 'active') {
      if (definition.runtime.alwaysOn
        || !canConfigure(definition, 'overview', 'edit', 'active')) {
        throw new Error(`Domain activation is centrally locked: ${domainId}`);
      }
      continue;
    }
    if (overviewKeys.has(key)) {
      if (!canConfigure(definition, 'overview', 'edit', key)) {
        throw new Error(`Domain overview configuration is not permitted: ${key}`);
      }
      continue;
    }
    if (eventKeys.has(key)) {
      if (definition.configurationPolicy.event.adapter !== 'canonical-fields'
        || !canConfigure(definition, 'event', 'edit')) {
        throw new Error(`Domain event configuration uses its protected editor: ${domainId}`);
      }
      continue;
    }
    if (key === 'surfaces') {
      if (!normalisedUpdates.surfaces || typeof normalisedUpdates.surfaces !== 'object'
        || Array.isArray(normalisedUpdates.surfaces)) {
        throw new Error('Domain surfaces configuration must be an object');
      }
      const changedSurfaces = Object.entries(normalisedUpdates.surfaces)
        .filter(([surface, value]) => compareDomainConfigs(
          currentEffective.surfaces?.[surface],
          value,
          { ignore: [] },
        ).length > 0);
      for (const [surface, value] of changedSurfaces) {
        if (surface === 'quickIssue') {
          const quickIssueSettings = new Set(['enabled', 'notes', 'attachments']);
          const inheritedStructure = Object.fromEntries(Object.entries(
            currentEffective.surfaces?.quickIssue || {},
          ).filter(([setting]) => !quickIssueSettings.has(setting)));
          const requestedStructure = Object.fromEntries(Object.entries(value || {})
            .filter(([setting]) => !quickIssueSettings.has(setting)));
          if (compareDomainConfigs(
            inheritedStructure,
            requestedStructure,
            { ignore: [] },
          ).length > 0) {
            throw new Error('Domain Quick Issue structure is centrally declared');
          }
          for (const setting of ['enabled', 'notes', 'attachments']) {
            if (value?.[setting] === currentEffective.surfaces?.quickIssue?.[setting]) continue;
            if (typeof value?.[setting] !== 'boolean'
              || !canConfigure(
                definition,
                'event',
                'edit',
                `presentation.quickIssue.${setting}`,
              )) {
              throw new Error(`Domain Quick Issue configuration is not permitted: ${setting}`);
            }
          }
          continue;
        }
        if (surface === 'classDisplay') {
          const inheritedStructure = { ...(currentEffective.surfaces?.classDisplay || {}) };
          const requestedStructure = { ...(value || {}) };
          delete inheritedStructure.enabled;
          delete requestedStructure.enabled;
          if (compareDomainConfigs(
            inheritedStructure,
            requestedStructure,
            { ignore: [] },
          ).length > 0) {
            throw new Error('Domain class display structure is centrally declared');
          }
          if (typeof value?.enabled !== 'boolean'
            || !canConfigure(
              definition,
              'event',
              'edit',
              'presentation.classDisplay.enabled',
            )) {
            throw new Error('Domain class display configuration is not permitted');
          }
          continue;
        }
        const capabilityBySurface = {
          rules: 'rules',
          dashboard: 'collective',
          reports: 'collective',
        };
        const capability = capabilityBySurface[surface];
        if (capability
          && normalisedUpdates.capabilities
          && Boolean(value?.enabled)
            === capabilityIsEnabled({ capabilities: normalisedUpdates.capabilities }, capability)) {
          continue;
        }
        throw new Error(`Domain surface is centrally declared: ${surface}`);
      }
      continue;
    }
    if (actionDefinitionKeys.has(key)) {
      if (definition.configurationPolicy.actions.adapter !== 'action-definitions'
        || !canConfigure(definition, 'actions', 'edit')) {
        throw new Error(`Domain Action configuration uses its protected editor: ${domainId}`);
      }
      continue;
    }
    if (parentMessageKeys.has(key)) {
      if (definition.configurationPolicy.capabilities.messages !== 'school') {
        throw new Error(`Domain parent messages are not school-configurable: ${domainId}`);
      }
      continue;
    }
    if (key === 'reporting') {
      if (!canConfigure(definition, 'reports', 'edit')) {
        throw new Error(`Domain reporting configuration is not permitted: ${domainId}`);
      }
      continue;
    }
    if (key === 'capabilities' || compatibilityKeys.has(key)) continue;
    throw new Error(`Unsupported governed Domain configuration: ${key}`);
  }

  if (normalisedUpdates.capabilities) {
    for (const capability of Object.keys(normalisedUpdates.capabilities)) {
      const before = currentEffective.capabilities?.[capability];
      const after = normalisedUpdates.capabilities[capability];
      if (compareDomainConfigs(before, after, { ignore: [] }).length > 0
        && !canConfigureDomainCapability(definition, capability)) {
        throw new Error(`Domain capability is centrally declared: ${capability}`);
      }
    }
  }

  // Firestore permits only these narrow installation toggles directly. All
  // configuration, governance and materialised structural paths are owned by
  // the authenticated server transaction below.
  const updateKeys = Object.keys(normalisedUpdates);
  const directlyWritableKeys = new Set(['active', 'storiesEnabled']);
  if (updateKeys.length > 0
    && updateKeys.every((key) => directlyWritableKeys.has(key))) {
    await updateDoc(domainDocRef(schoolId, domainId), {
      ...Object.fromEntries(updateKeys.map((key) => [key, Boolean(normalisedUpdates[key])])),
      updatedAt: Timestamp.now(),
    });
    return;
  }

  const desired = {
    ...currentEffective,
    ...Object.fromEntries(Object.entries(normalisedUpdates)
      .filter(([key]) => !compatibilityKeys.has(key) && !actionKeys.has(key))),
    ...(Object.prototype.hasOwnProperty.call(normalisedUpdates, 'actionDefinitions')
      ? { actionDefinitions: normalisedUpdates.actionDefinitions }
      : {}),
    ...(Object.prototype.hasOwnProperty.call(normalisedUpdates, 'parentMessages')
      ? { parentMessages: normalisedUpdates.parentMessages }
      : {}),
  };
  if (writesCanonical) delete desired.fields;
  const configuration = deriveDomainConfiguration(definition, desired);
  const nextGovernance = {
    ...currentEffective.governance,
    modelId: CENTRAL_DOMAINS_MODEL.id,
    modelVersion: CENTRAL_DOMAINS_MODEL.version,
    definitionId: definition.id,
    definitionVersion: definition.definitionVersion,
    appliedModelVersion: CENTRAL_DOMAINS_MODEL.version,
    appliedDefinitionVersion: definition.definitionVersion,
    prototypeId: definition.prototypeId,
    prototypeVersion: definition.prototypeVersion,
    appliedPrototypeVersion: definition.prototypeVersion,
    definitionAuthority: currentEffective.governance?.definitionAuthority
      || (PROTECTED_DOMAIN_IDS.includes(domainId) ? 'central' : 'school'),
    configuration,
    configurationRevision: (currentEffective.governance?.configurationRevision || 0) + 1,
    status: 'current',
  };
  const nextConfig = resolveStoredGovernedDomain({
    ...current,
    id: domainId,
    active: Object.prototype.hasOwnProperty.call(normalisedUpdates, 'active')
      ? Boolean(normalisedUpdates.active)
      : currentEffective.active,
    configuration,
    governance: nextGovernance,
  }, { assumeCurrent: true });
  const committed = await commitDomainConfiguration({
    operation: 'update',
    schoolId,
    domainId,
    configuration: withoutUndefinedObjectValues(configuration),
    ...(Object.prototype.hasOwnProperty.call(normalisedUpdates, 'active')
      ? { active: nextConfig.active !== false }
      : {}),
    expectedConfigurationRevision:
      currentEffective.governance?.configurationRevision || 0,
  });
  return { primary: nextConfig.primary, followUp: nextConfig.followUp || [],
    configurationRevision: committed.configurationRevision ?? nextGovernance.configurationRevision };
}

/**
 * Toggle Stories auto-minting for a domain (school opt-in).
 *
 * Unlike {@link updateDomain}, this is allowed on protected domains
 * (discipline, recognition): it touches only the school-settable `storiesEnabled`
 * flag, never the canonical schema. The flag is read back through
 * `synthesizeDomains` (see SCHOOL_SETTABLE_PROTECTED_KEYS) and by the
 * server-side auto-mint trigger before it creates any Story.
 *
 * @param {string} schoolId
 * @param {string} domainId
 * @param {boolean} enabled
 * @returns {Promise<void>}
 */
export async function setDomainStoriesEnabled(schoolId, domainId, enabled) {
  if (!schoolId) throw new Error('setDomainStoriesEnabled: schoolId is required');
  if (!domainId) throw new Error('setDomainStoriesEnabled: domainId is required');
  if (!PROTECTED_DOMAIN_IDS.includes(domainId)) {
    const current = await getDomain(schoolId, domainId);
    if (!current || !capabilityIsEnabled(current, 'stories')) {
      throw new Error(`Stories capability is not enabled for Domain: ${domainId}`);
    }
  }
  await updateDoc(domainDocRef(schoolId, domainId), {
    storiesEnabled: !!enabled,
    updatedAt: Timestamp.now(),
  });
}

const COLLECTIVE_REPORTING_KEYS = Object.freeze([
  'parentWeeklySummary',
  'staffWeeklyBrief',
]);

/**
 * Set one school-owned reporting choice without detaching the Domain from its
 * central definition. Dashboard calculations and report shapes stay centrally
 * owned; the school controls only whether an authorised Domain is included in
 * a scheduled audience output.
 */
export async function setDomainCollectiveReporting(
  schoolId,
  domainId,
  reportingKey,
  enabled,
) {
  if (!schoolId) throw new Error('setDomainCollectiveReporting: schoolId is required');
  if (!domainId) throw new Error('setDomainCollectiveReporting: domainId is required');
  if (!COLLECTIVE_REPORTING_KEYS.includes(reportingKey)) {
    throw new Error(`Unknown Collective reporting option: ${reportingKey}`);
  }
  const current = await getStoredDomain(schoolId, domainId);
  if (!current) throw new Error(`Domain not found: ${domainId}`);
  const effective = resolveStoredGovernedDomain(current);
  if (!capabilityIsEnabled(effective, 'collective') || !domainSupportsSurface(effective, 'reports')) {
    throw new Error(`Collective reporting is not enabled for Domain: ${domainId}`);
  }

  const reporting = {
    parentWeeklySummary: false,
    staffWeeklyBrief: false,
    ...(effective.reporting || {}),
    [reportingKey]: Boolean(enabled),
  };
  await updateDomain(schoolId, domainId, { reporting });
}

/**
 * Switch a protected domain on or off for a school (setup-wizard deselection,
 * settings toggle).
 *
 * Like {@link setDomainStoriesEnabled}, this is the escape hatch for protected
 * domains: it writes only the school-settable `active` flag — the canonical
 * schema is untouched and `synthesizeDomains` merges the flag back on read.
 * Discipline and recognition are always on and cannot be deactivated;
 * user-created domains are managed through updateDomain/deleteDomain instead.
 *
 * @param {string} schoolId
 * @param {string} domainId - a protected domain id (attendance, health, …)
 * @param {boolean} active
 * @returns {Promise<void>}
 */
export async function setProtectedDomainActive(schoolId, domainId, active) {
  if (!schoolId) throw new Error('setProtectedDomainActive: schoolId is required');
  if (ALWAYS_ON_PROTECTED_IDS.includes(domainId)) {
    throw new Error(`setProtectedDomainActive: "${domainId}" is always on and cannot be deactivated`);
  }
  if (!getCentralDomainDefinition(domainId)) {
    throw new Error(`setProtectedDomainActive: "${domainId}" is not a centrally defined Domain`);
  }
  await updateDoc(domainDocRef(schoolId, domainId), {
    active: !!active,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Read the school-wide "allow parents & family to comment on Stories" flag.
 *
 * This is the school's socials-moderation opt-in: staff can always comment on
 * in-school Stories, but a parent/family commenter only gets through when this
 * is on (AND the post itself allows it). Opt-IN — off unless explicitly true.
 * The server enforces it in postStoryComment; this is just for the settings UI.
 *
 * @param {string} schoolId
 * @returns {Promise<boolean>}
 */
export async function getSchoolAllowParentComments(schoolId) {
  if (!schoolId) return false;
  const snap = await getDoc(doc(db, 'schools', schoolId));
  return snap.exists() && snap.data().allowParentStoryComments === true;
}

/**
 * Set the school-wide "allow parents & family to comment on Stories" flag.
 * @param {string} schoolId
 * @param {boolean} enabled
 * @returns {Promise<void>}
 */
export async function setSchoolAllowParentComments(schoolId, enabled) {
  if (!schoolId) throw new Error('setSchoolAllowParentComments: schoolId is required');
  await setDoc(doc(db, 'schools', schoolId), {
    allowParentStoryComments: !!enabled,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Resolve a domain's input fields. Returns the primary + any follow-up fields
 * for protected domains (new shape), or the legacy `fields[]` array for
 * user-created domains. Renderers that care about ONLY the headline input
 * should read `domain.primary` directly instead of filtering this list.
 *
 * @param {Object} domain - DomainConfig
 * @returns {Array<{id: string, label: string, type: string, order: number, maxScore?: number, options?: any}>}
 */
export function getDomainFields(domain) {
  if (!domain) return [];
  if (domain.primary) {
    const followUp = Array.isArray(domain.followUp) ? domain.followUp : [];
    const merged = [domain.primary, ...followUp].map((f, i) => ({ order: i, ...f }));
    return normaliseFields(merged);
  }
  if (Array.isArray(domain.fields) && domain.fields.length > 0) {
    const sorted = [...domain.fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return normaliseFields(sorted);
  }
  // Synthesize a single-field array from the legacy shape so multi-field
  // renderers can treat every domain identically.
  const type = domain.fieldType || 'multiChoice';
  const label = domain.terminology?.category?.label || domain.terminology?.type?.label || 'Value';
  return [{
    id: 'primary',
    label: label.charAt(0).toUpperCase() + label.slice(1),
    type,
    order: 0,
    ...(domain.maxScore != null ? { maxScore: domain.maxScore } : {}),
  }];
}

/**
 * Count events recorded against a domain. Used to gate destructive actions:
 * archiving an empty domain is a one-click op; archiving a domain with data
 * should require an explicit type-to-confirm.
 * @param {string} schoolId
 * @param {DomainConfig} domain - resolved domain (must have collections.events or eventsCollection)
 * @returns {Promise<number>}
 */
export async function getDomainEventCount(schoolId, domain) {
  if (!schoolId) throw new Error('getDomainEventCount: schoolId is required');
  if (!domain) throw new Error('getDomainEventCount: domain is required');
  const eventsPath = domain.collections?.events || domain.eventsCollection;
  if (!eventsPath) return 0;

  const eventsRef = collection(db, 'schools', schoolId, eventsPath);
  const eventsQuery = query(
    eventsRef,
    ...dynamicDomainListConstraints({
      collectionName: eventsPath,
      schoolId,
      domainId: domain.id,
    }),
  );
  try {
    const snap = await getCountFromServer(eventsQuery);
    return snap.data().count;
  } catch (err) {
    console.warn('[domainRegistry] getDomainEventCount failed:', err);
    return 0;
  }
}

/**
 * Soft-delete (archive) a domain. Rejects protected domains.
 * @param {string} schoolId
 * @param {string} domainId
 * @returns {Promise<void>}
 */
export async function deleteDomain(schoolId, domainId) {
  if (!schoolId) throw new Error('deleteDomain: schoolId is required');
  if (!domainId) throw new Error('deleteDomain: domainId is required');

  const current = await getDomain(schoolId, domainId);
  if (!current) throw new Error(`Domain not found: ${domainId}`);
  if (current.protected) {
    throw new Error(`Cannot delete protected domain: ${domainId}`);
  }

  const ref = domainDocRef(schoolId, domainId);
  await updateDoc(ref, {
    active: false,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Subscribe to real-time domain updates.
 * @param {string} schoolId
 * @param {Function} callback - (domains: DomainConfig[], issues: Array) => void
 * @param {Function} [onError] - reports a failed subscription separately from data
 * @returns {Function} Unsubscribe function
 */
export function subscribeDomains(schoolId, callback, onError) {
  if (!schoolId) {
    callback([], []);
    return () => {};
  }

  const ref = domainsRef(schoolId);
  const q = query(ref, orderBy('sortOrder'));

  return onSnapshot(q, (snap) => {
    const docs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
    const issues = [];
    const domains = synthesizeDomains(docs, (domainId, error) => {
      console.error('[domainRegistry] unavailable Domain:', domainId, error);
      issues.push({ domainId });
    });
    callback(domains, issues);
  }, (err) => {
    console.error('[domainRegistry] subscription error:', err);
    if (onError) onError(err);
    else callback(null, err);
  });
}

/**
 * Add event-type documents to a domain's types sub-collection.
 * @param {string} schoolId
 * @param {string} typesCollection - e.g. 'domain_late_arrivals_types'
 * @param {Array<string|{label: string, award?: {currencyId: string, amount: number}}>} types
 * @returns {Promise<void>}
 */
/**
 * Update a single type document in a domain's types sub-collection.
 */
export async function updateDomainType(
  schoolId,
  typesCollection,
  typeId,
  updates,
  domainId = null,
) {
  if (!schoolId || !typesCollection || !typeId) throw new Error('updateDomainType: missing required params');
  const ref = doc(db, 'schools', schoolId, typesCollection, typeId);
  await updateDoc(ref, {
    ...updates,
    schoolId,
    ...(domainId ? { domainId } : {}),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Retire a single type document from a domain's types sub-collection.
 * Stable type ids remain readable for historical records.
 */
export async function removeDomainType(schoolId, typesCollection, typeId) {
  if (!schoolId || !typesCollection || !typeId) throw new Error('removeDomainType: missing required params');
  const ref = doc(db, 'schools', schoolId, typesCollection, typeId);
  await updateDoc(ref, {
    active: false,
    retired: true,
    updatedAt: Timestamp.now(),
  });
}

export async function addDomainTypes(schoolId, typesCollection, types, domainId = null) {
  if (!schoolId) throw new Error('addDomainTypes: schoolId is required');
  if (!typesCollection) throw new Error('addDomainTypes: typesCollection is required');
  if (!Array.isArray(types) || types.length === 0) return;

  const now = Timestamp.now();
  const ref = collection(db, 'schools', schoolId, typesCollection);
  for (const entry of types) {
    // Accept both strings (backward-compat) and objects with award config
    if (typeof entry === 'string') {
      if (!entry.trim()) continue;
      await addDoc(ref, {
        label: entry.trim(),
        active: true,
        createdAt: now,
        schoolId,
        ...(domainId ? { domainId } : {}),
      });
    } else if (entry && entry.label?.trim()) {
      const doc = {
        label: entry.label.trim(),
        active: true,
        showInQueue: entry.showInQueue !== false,
        createdAt: now,
        schoolId,
        ...(domainId ? { domainId } : {}),
      };
      if (entry.award?.currencyId && entry.award?.amount) {
        doc.award = { currencyId: entry.award.currencyId, amount: entry.award.amount };
      }
      await addDoc(ref, doc);
    }
  }
}

export default {
  getDomains,
  getDomain,
  createDomain,
  updateDomain,
  deleteDomain,
  subscribeDomains,
  addDomainTypes,
  seedProtectedDomains,
};
